import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from "@mui/icons-material";
import {
  setFeatureFilesState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwirghtCSharpReqnrollSlice";
import FeatureFile from "../../../../../shared/FeatureFile";
import SessionExpired from "../../../../../../../../../../popup-components/SessionExpiredPopup";
import MessagePopup from "../../../../../../../../../../popup-components/MessagePopup";
import { useContext, useEffect, useState } from "react";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { downloadSingleFile } from "../../../../../../../../../../../utils/downloadUtils";

const ReqNRollFeatureFilePage = () => {
  const location = useLocation();
  const navigate = useNavigateWithSameKey();
  const dispatch = useDispatch();

  const { locationKey } = location.state;

  const { featureFiles, automationPath } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpReqNRoll"
      ] || {}
  );

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }
  }, [locationKey]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleContinueClick = () => {
    try {
      // Navigate to next step...
      navigate(`${automationPath}/page-dom`);
    } catch (error) {
      setMessage("Couldn't process your request please try again");
      setMessagePopup(true);
    }
  };

  const handleSaveContent = (content) => {
    dispatch(setFeatureFilesState(content));
  };

  const handleDownload = () => {
    let progressHandler = null;

    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Downloading ...");

      downloadSingleFile({ fileType: "feature", fileName: "Feature", fileContent: featureFiles });
    } catch (error) {
      setMessage("Unable to download the file, please try again");
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });

      if (progressHandler) {
        progressHandler.stop();
      }
    }
  };

  return (
    <Box sx={{ height: "100%" }}>
      {/* You can now display the feature file content */}
      {featureFiles ? (
        <FeatureFile
          title="Feature File"
          content={featureFiles}
          handleSaveContent={handleSaveContent}
        />
      ) : (
        <Box
          sx={{
            height: "100%",
            background: "#F6F3F2",
            display: "flex",
            flexDirection: "column",
            py: 4,
          }}
        >
          <Container sx={{ width: "100%", flex: 1 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mb: 4,
                fontWeight: "bold",
                color: "#333",
                fontSize: "28px",
                fontFamily: "Manrope",
              }}
            >
              Feature File
            </Typography>
            <Card
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                mb: 4,
                maxWidth: "100%",
              }}
            >
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontFamily: "Manrope", mb: 2 }}
                >
                  No data available. Please go back and generate feature files
                  first.
                </Typography>
              </CardContent>
            </Card>
          </Container>
        </Box>
      )}

      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "transparent",
          borderRadius: "0 0 8px 8px",
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackClick}
          sx={{
            height: "40px",
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            // fontFamily: "Manrope",
            fontWeight: 500,
            borderRadius: "8px",
            borderWidth: "2px",
            paddingTop: "8px",
            paddingRight: "16px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            gap: "8px",
            "&:hover": {
              borderColor: "#0094FB",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
            },
          }}
        >
          Back
        </Button>

        <Box className="flex-container-row" sx={{ gap: "8px" }}>
          {/* Download Button */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleDownload}
            disabled={!featureFiles || featureFiles.length === 0}
            sx={{
              height: "40px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              gap: "8px",
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
            }}
          >
            Download
          </Button>
          {/* Continue Button */}
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleContinueClick}
            disabled={!featureFiles}
            sx={{
              height: "40px",
              backgroundColor: "#0094FB",
              color: "white",
              textTransform: "none",
              // fontFamily: "Manrope",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              borderColor: "#0094FB",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              "&:hover": {
                backgroundColor: "#0078D4",
                borderColor: "#0078D4",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
          >
            Introduce SUT
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReqNRollFeatureFilePage;
